/**
 * Whiteboard UI Utilities
 * static/js/whiteboard/ui.js
 * 
 * UI 工具函數（Toast、對話框等）
 */

import { MESSAGES } from './config.js';

/**
 * 顯示 Toast 訊息
 * @param {string} message 
 * @param {string} type - 'success' | 'error' | 'warning' | 'info'
 * @param {number} duration - 毫秒
 */
export function showToast(message, type = 'info', duration = 3000) {
  // 建立 Toast 容器（如果不存在）
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'position-fixed bottom-0 end-0 p-3';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
  }

  // 建立 Toast 元素
  const toastId = `toast-${Date.now()}`;
  const bgClass = {
    success: 'bg-success',
    error: 'bg-danger',
    warning: 'bg-warning',
    info: 'bg-info',
  }[type] || 'bg-info';

  const textClass = type === 'warning' ? 'text-dark' : 'text-white';

  const toastHtml = `
    <div id="${toastId}" class="toast ${bgClass} ${textClass}" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="toast-body d-flex align-items-center">
        <span class="me-auto">${escapeHtml(message)}</span>
        <button type="button" class="btn-close btn-close-white ms-2" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;

  container.insertAdjacentHTML('beforeend', toastHtml);

  const toastElement = document.getElementById(toastId);
  
  // 使用 Bootstrap Toast
  if (window.bootstrap && window.bootstrap.Toast) {
    const toast = new window.bootstrap.Toast(toastElement, {
      autohide: true,
      delay: duration,
    });
    toast.show();

    // 移除 DOM
    toastElement.addEventListener('hidden.bs.toast', () => {
      toastElement.remove();
    });
  } else {
    // 無 Bootstrap 時的簡易實現
    toastElement.style.display = 'block';
    setTimeout(() => {
      toastElement.remove();
    }, duration);
  }
}

/**
 * HTML 跳脫（防止 XSS）
 * @param {string} text 
 * @returns {string}
 */
export function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * 顯示確認對話框
 * @param {string} message 
 * @returns {Promise<boolean>}
 */
export function showConfirm(message) {
  return new Promise((resolve) => {
    // 使用 Bootstrap Modal（如果存在）
    const existingModal = document.getElementById('confirm-modal');
    if (existingModal && window.bootstrap) {
      const messageEl = existingModal.querySelector('.modal-body');
      const confirmBtn = existingModal.querySelector('.btn-confirm');
      
      if (messageEl && confirmBtn) {
        messageEl.textContent = message;
        
        const modal = new window.bootstrap.Modal(existingModal);
        
        const handleConfirm = () => {
          modal.hide();
          confirmBtn.removeEventListener('click', handleConfirm);
          resolve(true);
        };
        
        const handleHidden = () => {
          existingModal.removeEventListener('hidden.bs.modal', handleHidden);
          resolve(false);
        };
        
        confirmBtn.addEventListener('click', handleConfirm);
        existingModal.addEventListener('hidden.bs.modal', handleHidden);
        
        modal.show();
        return;
      }
    }
    
    // 回退到原生 confirm
    resolve(window.confirm(message));
  });
}

/**
 * 顯示輸入對話框
 * @param {string} title 
 * @param {string} defaultValue 
 * @returns {Promise<string|null>}
 */
export function showPrompt(title, defaultValue = '') {
  return new Promise((resolve) => {
    // 使用 Bootstrap Modal（如果存在）
    const existingModal = document.getElementById('prompt-modal');
    if (existingModal && window.bootstrap) {
      const titleEl = existingModal.querySelector('.modal-title');
      const inputEl = existingModal.querySelector('input');
      const confirmBtn = existingModal.querySelector('.btn-confirm');
      
      if (titleEl && inputEl && confirmBtn) {
        titleEl.textContent = title;
        inputEl.value = defaultValue;
        
        const modal = new window.bootstrap.Modal(existingModal);
        
        const handleConfirm = () => {
          const value = inputEl.value.trim();
          modal.hide();
          confirmBtn.removeEventListener('click', handleConfirm);
          resolve(value || null);
        };
        
        const handleHidden = () => {
          existingModal.removeEventListener('hidden.bs.modal', handleHidden);
          resolve(null);
        };
        
        confirmBtn.addEventListener('click', handleConfirm);
        existingModal.addEventListener('hidden.bs.modal', handleHidden);
        
        modal.show();
        inputEl.focus();
        inputEl.select();
        return;
      }
    }
    
    // 回退到原生 prompt
    const result = window.prompt(title, defaultValue);
    resolve(result ? result.trim() : null);
  });
}

/**
 * 更新載入狀態
 * @param {boolean} loading 
 */
export function setLoadingState(loading) {
  const loadingOverlay = document.getElementById('loading-overlay');
  if (loadingOverlay) {
    loadingOverlay.style.display = loading ? 'flex' : 'none';
  }
}

/**
 * 更新自動儲存指示器
 * @param {string} status - 'saved' | 'saving' | 'error' | 'dirty' | 'disabled'
 */
export function updateAutosaveIndicator(status) {
  const indicator = document.getElementById('autosave-indicator');
  if (!indicator) return;

  const statusConfig = {
    saved: { text: '已儲存', class: 'text-success', icon: '✓' },
    saving: { text: '儲存中...', class: 'text-muted', icon: '⏳' },
    error: { text: '儲存失敗', class: 'text-danger', icon: '✗' },
    dirty: { text: '未儲存', class: 'text-warning', icon: '•' },
    disabled: { text: '自動儲存：關', class: 'text-secondary', icon: '' },
  };

  const config = statusConfig[status] || statusConfig.dirty;
  indicator.innerHTML = `<span class="${config.class}">${config.icon} ${config.text}</span>`;
}

/**
 * 更新白板列表 UI
 * @param {Array} whiteboards 
 * @param {number|null} currentId 
 */
export function updateWhiteboardList(whiteboards, currentId = null) {
  const listContainer = document.getElementById('whiteboard-list');
  if (!listContainer) return;

  if (whiteboards.length === 0) {
    listContainer.innerHTML = '<div class="text-muted text-center py-3">尚無儲存的白板</div>';
    return;
  }

  const html = whiteboards.map(wb => `
    <div class="whiteboard-item ${wb.id === currentId ? 'active' : ''}" 
         data-id="${wb.id}" 
         role="button" 
         tabindex="0">
      <div class="d-flex align-items-start">
        ${wb.thumbnail_base64 
          ? `<img src="${wb.thumbnail_base64}" class="whiteboard-thumbnail me-2" alt="" />` 
          : '<div class="whiteboard-thumbnail-placeholder me-2">📝</div>'
        }
        <div class="flex-grow-1 overflow-hidden">
          <div class="whiteboard-title text-truncate">${escapeHtml(wb.title)}</div>
          <div class="whiteboard-date text-muted small">${formatDate(wb.updated_at)}</div>
        </div>
      </div>
    </div>
  `).join('');

  listContainer.innerHTML = html;
}

/**
 * 格式化日期
 * @param {string} dateStr 
 * @returns {string}
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins} 分鐘前`;
    if (diffHours < 24) return `${diffHours} 小時前`;
    if (diffDays < 7) return `${diffDays} 天前`;
    
    return date.toLocaleDateString('zh-TW');
  } catch {
    return dateStr;
  }
}
